import type { useReactTable } from '@tanstack/react-table';
import { useLayoutEffect, useRef, useState } from 'react';
import { useCallback } from 'react';
import { flushSync } from 'react-dom';
import { useSafeLocalStorage, useStorageKey } from '../../../../tools/storage.js';

export function useColumnSizes<TData>(id: string, table: ReturnType<typeof useReactTable<TData>>) {
    const containerRef = useRef<HTMLDivElement>(null);
    const tableRef = useRef<HTMLTableElement>(null);

    const hasStoredColumnSizes = useRef(false);

    const localStorageKey = useStorageKey({
        base: ' resizable-table',
        utility: 'column-sizes',
        id,
        version: 1,
    });

    const [columnSizes, setColumnSizes] = useSafeLocalStorage<Record<string, number>>(
        localStorageKey,
        (serializedData) => {
            if (typeof serializedData === 'string') {
                hasStoredColumnSizes.current = true;
            }

            return table.getHeaderGroups().reduce((acc, headerGroup) => {
                return {
                    ...acc,
                    ...headerGroup.headers.reduce((acc, header) => {
                        return { ...acc, [header.id]: header.getSize() };
                    }, {}),
                };
            }, {});
        }
    );

    const [hasInitialized, setHasInitialized] = useState(false);

    const fitColumnsToContainer = useCallback(() => {
        const containerEl = containerRef.current;
        const tableEl = tableRef.current;

        if (!containerEl || !tableEl) {
            return true;
        }

        const containerWidth = containerEl.clientWidth;
        const tableWidth = tableEl.clientWidth;

        if (containerWidth <= tableWidth) {
            return true;
        }

        setColumnSizes((prevColumnSizes: Record<string, number>) => {
            const resizableColumns = table
                .getHeaderGroups()
                .flatMap((headerGroup) => {
                    return headerGroup.headers.filter((header) => {
                        return header.column.getCanResize();
                    });
                })
                .map((header) => {
                    return {
                        columnId: header.id,
                        currentSize: header.getSize(),
                    };
                });

            const sizeDiff = containerWidth - tableWidth;
            const columnCount = resizableColumns.length;
            const additionalSpace = sizeDiff / columnCount;

            return Object.entries(prevColumnSizes).reduce(
                (acc, [columnId, currentSize]) => {
                    if (resizableColumns.some((column) => column.columnId === columnId)) {
                        acc[columnId] = currentSize + additionalSpace;
                    } else {
                        acc[columnId] = currentSize;
                    }

                    return acc;
                },
                {} as Record<string, number>
            );
        });

        return true;
    }, [setColumnSizes, table]);

    const _resetColumns = useCallback(() => {
        setColumnSizes(
            table.getHeaderGroups().reduce((acc, headerGroup) => {
                return {
                    ...acc,
                    ...headerGroup.headers.reduce((acc, header) => {
                        return { ...acc, [header.id]: header.getSize() };
                    }, {}),
                };
            }, {})
        );
    }, [setColumnSizes, table]);

    const resetColumnSizes = useCallback(() => {
        // reset the column sizes
        flushSync(_resetColumns);

        // fit the columns to the container
        setTimeout(fitColumnsToContainer);

        // remove persisted state
        localStorage.removeItem(localStorageKey);
    }, [fitColumnsToContainer, localStorageKey, _resetColumns]);

    useLayoutEffect(() => {
        setHasInitialized((prevHasInitialized) => {
            return hasStoredColumnSizes.current || prevHasInitialized ? true : fitColumnsToContainer();
        });
    }, []);

    return {
        hasInitialized,
        containerRef,
        tableRef,
        columnSizes,
        setColumnSizes,
        resetColumnSizes,
    };
}
